import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Layout,
  Button,
  Typography,
  Row,
  Col,
  Card,
  Space,
  Divider,
  Tag,
  Timeline,
} from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  EnvironmentOutlined,
  EuroCircleOutlined,
  HeartOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import PublicHeader from "../components/PublicHeader";
import portrait from "../assets/patrick-sauter.jpg";

const { Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

const COLORS = {
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  pageBg: "#F4FBFC",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
  success: "#52c41a",
};

const principles = [
  {
    icon: <EuroCircleOutlined />,
    title: "Optimieren, ohne am Menschen zu sparen",
    text: "Smart Routing macht Touren effizienter, qualitativer und kostengünstiger. Die Leistung bleibt erhalten; optimiert werden Planung, Wege und Abläufe.",
  },
  {
    icon: <EnvironmentOutlined />,
    title: "Strukturierte Planung",
    text: "Geodaten, Regeln und Kapazitäten werden so zusammengeführt, dass aus vielen Einzelinformationen ein nachvollziehbarer Fahrplan entsteht.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Persönliche Verantwortung",
    text: "Hinter Smart Routing steht kein anonymes System, sondern ein direkter Ansprechpartner für Einrichtung, Rückfragen und Weiterentwicklung.",
  },
];

const focusPoints = [
  "Digitale Abbildung der Tourenplanung von der Datenerfassung bis zum PDF-Fahrplan",
  "Berücksichtigung von Fahrzeugkapazitäten, Rollstuhlplätzen, Begleitpersonen und Wochentagen",
  "Kombination aus Geodatenanalyse, regelbasierter Planung und algorithmischer Optimierung",
  "Persönliche Unterstützung beim Aufbau sauberer Stammdaten und erster Planungsprozesse",
];

const customerGroups = [
  "Fahrdienste",
  "Soziale Träger",
  "Kostenträger",
  "Werkstätten",
];

const timelineItems = [
  {
    color: COLORS.primaryDark,
    children: (
      <>
        <Text strong>Entwicklung von Smart Routing</Text>
        <Paragraph style={{ margin: "4px 0 0", color: COLORS.muted }}>
          Aufbau einer Software, die Tourenplanung für die Personenbeförderung
          effizienter, transparenter und besser nachvollziehbar macht.
        </Paragraph>
      </>
    ),
  },
  {
    color: COLORS.primaryDark,
    children: (
      <>
        <Text strong>Sozialplanung im Landkreis Tübingen</Text>
        <Paragraph style={{ margin: "4px 0 0", color: COLORS.muted }}>
          Erfahrung mit Planung, Abstimmung und fachlichen Anforderungen im
          sozialen Bereich.
        </Paragraph>
      </>
    ),
  },
  {
    color: COLORS.primaryDark,
    children: (
      <>
        <Text strong>Regionalleitung bei der Habila in Tübingen</Text>
        <Paragraph style={{ margin: "4px 0 0", color: COLORS.muted }}>
          Einblick in organisatorische Verantwortung, soziale Dienstleistungen
          und Abläufe, die im Alltag funktionieren müssen.
        </Paragraph>
      </>
    ),
  },
];

export default function UeberMich() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Über Patrick Sauter – Smart Routing";
  }, []);

  const heroBackground = [
    `radial-gradient(900px 460px at 12% -10%, ${COLORS.primaryLight} 0%, transparent 60%)`,
    "radial-gradient(900px 460px at 110% 0%, #FFFFFF 0%, transparent 55%)",
    `linear-gradient(180deg, #FFFFFF 0%, ${COLORS.pageBg} 100%)`,
  ].join(", ");

  const goHomeTop = () => {
    navigate("/");
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 120);
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#FFFFFF" }}>
      <PublicHeader />

      <Content>
        <section style={{ background: heroBackground, padding: "64px 16px 56px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Row gutter={[34, 34]} align="middle">
              <Col xs={24} lg={13}>
                <Tag
                  style={{
                    background: COLORS.primaryLight,
                    color: COLORS.primaryDark,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 999,
                    padding: "4px 14px",
                    fontWeight: 600,
                    marginBottom: 16,
                    whiteSpace: "normal",
                  }}
                >
                  Effizienter planen, ohne Leistung zu kürzen.
                </Tag>

                <Title
                  level={1}
                  style={{
                    margin: "0 0 16px",
                    color: COLORS.text,
                    fontSize: "clamp(34px, 5vw, 54px)",
                    lineHeight: 1.08,
                    fontWeight: 900,
                  }}
                >
                  Über Patrick Sauter und Smart Routing
                </Title>

                <Paragraph
                  style={{
                    fontSize: 18,
                    color: COLORS.muted,
                    maxWidth: 720,
                    marginBottom: 24,
                  }}
                >
                  Ich entwickelte Smart Routing, weil gute Tourenplanung in sozialen
                  und organisatorischen Kontexten mehr braucht als eine Karte.
                  Mein Ziel ist eine Planung, die Kosten senkt und Qualität steigert,
                  ohne die Leistung für die beförderten Menschen zu reduzieren.
                </Paragraph>

                <Space size="middle" wrap>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    onClick={() => navigate("/contact")}
                  >
                    Kontakt aufnehmen
                  </Button>

                  <Button size="large" onClick={goHomeTop}>
                    Smart Routing ansehen
                  </Button>
                </Space>
              </Col>

              <Col xs={24} lg={11}>
                <div
                  style={{
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 20,
                    overflow: "hidden",
                    background: "#FFFFFF",
                    boxShadow: "0 18px 44px rgba(31, 78, 87, 0.10)",
                  }}
                >
                  <img
                    src={portrait}
                    alt="Patrick Sauter"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "min(620px, 72vw)",
                      objectFit: "cover",
                      objectPosition: "50% 28%",
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <section style={{ padding: "58px 16px 36px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Row gutter={[18, 18]}>
              {principles.map((item) => (
                <Col xs={24} md={8} key={item.title}>
                  <Card
                    style={{
                      height: "100%",
                      borderRadius: 18,
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: "0 8px 24px rgba(31, 78, 87, 0.06)",
                    }}
                  >
                    <Space direction="vertical" size={10}>
                      <div
                        style={{
                          width: 46,
                          height: 46,
                          borderRadius: 14,
                          background: COLORS.primaryLight,
                          color: COLORS.primaryDark,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 24,
                        }}
                      >
                        {item.icon}
                      </div>
                      <Title level={4} style={{ margin: 0, color: COLORS.text }}>
                        {item.title}
                      </Title>
                      <Paragraph style={{ margin: 0, color: COLORS.muted }}>
                        {item.text}
                      </Paragraph>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        <section style={{ padding: "28px 16px 64px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Row gutter={[22, 22]} align="top">
              <Col xs={24} lg={13}>
                <Card
                  style={{
                    height: "100%",
                    borderRadius: 20,
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.pageBg,
                  }}
                >
                  <Space direction="vertical" size={16}>
                    <Title level={2} style={{ margin: 0, color: COLORS.text }}>
                      Warum ich Smart Routing entwickelte
                    </Title>

                    <Paragraph style={{ margin: 0, color: COLORS.muted, fontSize: 16 }}>
                      Ich habe Smart Routing gebaut, um Tourenplanung effizienter,
                      qualitativer und kostengünstiger zu machen. Dabei geht es nicht
                      darum, am Menschen zu sparen. Die Leistung bleibt vollständig
                      erhalten; optimiert werden die Planung, die Zuordnung und die
                      Fahrwege.
                    </Paragraph>

                    <Paragraph style={{ margin: 0, color: COLORS.muted, fontSize: 16 }}>
                      Smart Routing verbindet technische Optimierung mit einem Blick für
                      die organisatorische Realität. Ziel ist nicht nur ein berechneter
                      Weg, sondern ein Plan, der für Verwaltung, Fahrerinnen und Fahrer
                      sowie die betreuten Menschen nachvollziehbar bleibt.
                    </Paragraph>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} lg={11}>
                <Card
                  style={{
                    height: "100%",
                    borderRadius: 20,
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Title level={3} style={{ marginTop: 0, color: COLORS.text }}>
                    Die Vorteile von Smart Routing
                  </Title>

                  <Space direction="vertical" size={12}>
                    {focusPoints.map((item) => (
                      <div key={item} style={{ display: "flex", gap: 10 }}>
                        <CheckOutlined style={{ color: COLORS.success, marginTop: 4 }} />
                        <span>{item}</span>
                      </div>
                    ))}
                  </Space>

                  <Divider style={{ margin: "18px 0" }} />

                  <Title level={4} style={{ marginTop: 0, color: COLORS.text }}>
                    Besonders relevant für
                  </Title>

                  <Space wrap>
                    {customerGroups.map((item) => (
                      <Tag key={item}>{item}</Tag>
                    ))}
                  </Space>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        <section style={{ padding: "0 16px 64px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Row gutter={[22, 22]} align="middle">
              <Col xs={24} lg={10}>
                <Title level={2} style={{ marginBottom: 12, color: COLORS.text }}>
                  Fachlicher Hintergrund
                </Title>
                <Paragraph style={{ color: COLORS.muted, fontSize: 16 }}>
                  Mein Hintergrund liegt an der Schnittstelle von Sozialem,
                  Organisation und digitaler Strukturierung. Diese Perspektive prägt
                  Smart Routing: Die Software soll nicht nur technisch funktionieren,
                  sondern für Menschen im Arbeitsalltag verständlich und hilfreich sein.
                </Paragraph>
                <Paragraph style={{ color: COLORS.muted, fontSize: 16 }}>
                  Besonders prägend waren dabei Aufgaben in regionaler Verantwortung
                  und in der Sozialplanung. Sie haben meinen Blick dafür geschärft,
                  dass gute digitale Werkzeuge fachliche Realität ernst nehmen müssen.
                </Paragraph>
              </Col>

              <Col xs={24} lg={14}>
                <Card
                  style={{
                    borderRadius: 20,
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Timeline items={timelineItems} />
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        <section style={{ padding: "0 16px 64px" }}>
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <Card
              style={{
                borderRadius: 20,
                border: `1px solid ${COLORS.border}`,
                background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, #FFFFFF 62%)`,
              }}
            >
              <Row gutter={[18, 18]} align="middle">
                <Col xs={24} md={15}>
                  <Space direction="vertical" size={10}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <HeartOutlined style={{ color: COLORS.primaryDark, fontSize: 28, flexShrink: 0 }} />
                      <Title level={3} style={{ margin: 0, color: COLORS.text }}>
                        Persönlich klären, was zu Ihrem Fahrdienst passt
                      </Title>
                    </div>
                    <Paragraph style={{ margin: 0, color: COLORS.muted }}>
                      Nehmen Sie einfach Kontakt zu mir auf. Gemeinsam besprechen wir
                      Ihre Anforderungen. Ich prüfe individuell, wie Smart Routing
                      zu Ihrer Organisation passt, und berate Sie persönlich.
                    </Paragraph>
                  </Space>
                </Col>

                <Col xs={24} md={9} style={{ textAlign: "center" }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    onClick={() => navigate("/contact")}
                  >
                    Kontakt aufnehmen
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        </section>
      </Content>

      <Footer
        style={{
          background: "#FFFFFF",
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24}>
              <Text style={{ color: COLORS.muted }}>
                © {new Date().getFullYear()} Patrick Sauter - Smart Routing
              </Text>
              <Divider type="vertical" />
              <Link to="/ueber-mich">Über mich</Link>
              <Divider type="vertical" />
              <Link to="/impressum">Impressum</Link>
              <Divider type="vertical" />
              <Link to="/datenschutz">Datenschutz</Link>
              <Divider type="vertical" />
              <Link to="/contact">Kontakt</Link>
            </Col>
          </Row>
        </div>
      </Footer>
    </Layout>
  );
}
